/**
 * Read a number the way the person in front of the tool writes one.
 *
 * `parseFloat("2,5")` returns `2`. Not `NaN` — `2`. It stops at the comma, keeps what
 * it has, and hands back a confidently wrong answer with no error anywhere. Every
 * calculator on this site did that, and for as long as the tools were English-only it
 * was a bug that mostly went unhit. The tool pages publish in Spanish, Portuguese,
 * French, German, Italian, Dutch, Polish, Turkish, Russian, Indonesian and Vietnamese
 * now — eleven languages that all write two-and-a-half as `2,5` — so we are inviting
 * exactly the input that breaks. A GPA tool that reads `3,7` as `3` is worse than one
 * that refuses it.
 *
 * It was found by a translator, not by review, and the way it surfaced is worth keeping:
 * they were localising `decimal-to-fraction`'s worked examples, noticed that writing
 * `0,375` the way their language writes it would hand the tool an input it rejects, and
 * had to leave the documentation in English decimals to keep the page self-consistent.
 * A page that cannot use its own conventions in its own examples is a page describing a
 * broken tool.
 *
 * ## The ambiguity, and why it is resolved this way
 *
 * `1,234` is 1234 to an English reader and 1.234 to a French one, and nothing in the
 * string says which. Some guess is unavoidable, so the rule is chosen by which mistake
 * the reader can see:
 *
 * - A lone separator is the decimal mark, whichever one it is. `2,5` is 2.5, `1.234` is
 *   1.234, `1,234` is 1.234.
 * - A repeated separator is grouping. `1.234.567` and `1,234,567` are both 1234567 —
 *   no notation uses two decimal points.
 * - With both present, the LAST one is the decimal mark and the other is grouping. That
 *   is true in every notation: `1,234.56` and `1.234,56` are both 1234.56.
 * - Spaces of every kind are grouping and are removed. French groups with U+202F and
 *   Russian with U+00A0, and both arrive by copy-paste.
 *
 * Only the first rule is a guess, and it guesses toward the visible failure. Someone who
 * meant 1234 and gets 1.234 is off by a factor of a thousand and sees it immediately;
 * `2,5` read as `2` is off by 20% and looks like an answer. Prefer the wrong result that
 * announces itself. Someone who means 1234 can also just type `1234`, which is what
 * people type anyway — grouping separators are a display convention far more than an
 * input one.
 *
 * A leading zero settles the ambiguous-looking case that matters most in practice:
 * `0,375` has three digits after the separator, which is what grouping looks like, but
 * no grouped number begins with a zero group. It reads as 0.375 under the rules above
 * without needing a special case, which is worth stating because it is the exact input
 * that started this.
 *
 * ## Scope
 *
 * This imports nothing. The widgets are client components and this has to be free to
 * land in any of them.
 *
 * It is for a field a person types a number into. It is NOT for parsing numbers the
 * site generated itself — those are already `.` decimals and `parseFloat` is correct
 * and cheaper. Nor is it a general locale-aware parser: it does not take a locale,
 * deliberately, because the reader's keyboard, their clipboard and the page's language
 * disagree constantly and a field that accepts every notation needs no locale to be
 * right.
 */

/** Every space that appears as a digit-grouping separator, plus the ordinary one. */
const GROUPING_SPACES = /[\s   ]/g;

/**
 * Minus signs that are not the ASCII hyphen-minus: the true minus U+2212, which Word,
 * PDFs and typeset papers use and which several locales' own number formatting emits;
 * the small (U+FE63) and fullwidth (U+FF0D) forms from CJK input. Read as "-" — before
 * this, "−3" pasted from a paper's table was not a number at all, and a data list
 * dropped it without a word.
 */
const MINUS_SIGNS = /[\u2212\uFE63\uFF0D]/g;

/** What is left must be digits, one leading sign, and one decimal point. */
const NORMALIZED_NUMBER = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/;

/**
 * A digit run that is validly grouped: 1-3 digits, then groups of exactly 3.
 *
 * Removing grouping separators without checking the groups is how "12.5.2024" — a
 * German date — became the number 1252024, and "1.2.3" became 123. Both are the silent
 * wrong answer this module exists to prevent, produced by the module itself: a repeated
 * separator was read as grouping on the strength of being repeated, which "1.234.567"
 * and "12.5.2024" satisfy equally.
 *
 * A number whose groups are the wrong size is not a number this parser should guess at.
 */
const GROUPED_INTEGER = /^\d{1,3}(?:SEP\d{3})+$/;

function groupedIntegerPattern(separator: "." | ","): RegExp {
  return new RegExp(
    GROUPED_INTEGER.source.replace("SEP", separator === "." ? "\\." : ",")
  );
}

/**
 * Drop the grouping separator from an integer run, or refuse if the groups are wrong.
 *
 * A run with no separator at all passes straight through — "1234" is a number people
 * type far more often than "1.234".
 */
function ungroup(run: string, separator: "." | ","): string | null {
  if (!run.includes(separator)) {
    return run;
  }
  const sign = run.startsWith("+") || run.startsWith("-") ? run[0] : "";
  const digits = sign ? run.slice(1) : run;
  if (!groupedIntegerPattern(separator).test(digits)) {
    return null;
  }
  return sign + digits.split(separator).join("");
}

/**
 * Rewrite a typed number as a machine one, or return null if it is not a number.
 *
 * Returns the normalized string rather than a number so a caller that wants to keep
 * digits exactly as typed (a big integer, a trailing zero) can. Most callers want
 * `parseNumberInput`.
 */
export function normalizeNumberInput(raw: string): string | null {
  const trimmed = raw.replace(GROUPING_SPACES, "").replace(MINUS_SIGNS, "-");
  if (trimmed === "") {
    return null;
  }
  const decimalMark = pickDecimalMark(trimmed);
  const normalized =
    decimalMark === null
      ? ungroupEither(trimmed)
      : stripAllBut(trimmed, decimalMark);
  if (normalized === null) {
    return null;
  }
  return NORMALIZED_NUMBER.test(normalized) ? normalized : null;
}

/**
 * Which of the two separators, if either, is acting as the decimal point.
 *
 * Null means neither is: the string has no separators at all, or the only ones present
 * are repeated and therefore grouping.
 */
function pickDecimalMark(value: string): "." | "," | null {
  const lastDot = value.lastIndexOf(".");
  const lastComma = value.lastIndexOf(",");
  if (lastDot === -1 && lastComma === -1) {
    return null;
  }
  if (lastDot !== -1 && lastComma !== -1) {
    return lastDot > lastComma ? "." : ",";
  }
  const mark = lastDot === -1 ? "," : ".";
  return countOf(value, mark) === 1 ? mark : null;
}

function countOf(value: string, character: string): number {
  let total = 0;
  for (const each of value) {
    if (each === character) {
      total += 1;
    }
  }
  return total;
}

/** No decimal mark: whichever separator is present is grouping, and must be valid. */
function ungroupEither(value: string): string | null {
  if (value.includes(".")) {
    return ungroup(value, ".");
  }
  if (value.includes(",")) {
    return ungroup(value, ",");
  }
  return value;
}

/** Keep one separator as the decimal point and drop the grouping ones. */
function stripAllBut(value: string, mark: "." | ","): string | null {
  const other = mark === "." ? "," : ".";
  const at = value.lastIndexOf(mark);
  const integerPart = ungroup(value.slice(0, at), other);
  if (integerPart === null) {
    return null;
  }
  return `${integerPart}.${value.slice(at + 1)}`;
}

/**
 * A typed number as a number, or null.
 *
 * Null rather than NaN because NaN propagates: `NaN > 0` is false, `NaN.toFixed(2)` is
 * `"NaN"`, and a widget that forgets to check renders the string "NaN" into the page.
 * Null is a type error at the first place it is forgotten.
 */
export function parseNumberInput(raw: string): number | null {
  const normalized = normalizeNumberInput(raw);
  if (normalized === null) {
    return null;
  }
  const value = Number(normalized);
  return Number.isFinite(value) ? value : null;
}

/**
 * A typed number, or a fallback — the shape most widgets already had.
 *
 * `parseFloat(x) || 0` was the common idiom, and it is wrong twice over: it misreads
 * comma decimals, and `|| 0` also swallows a legitimate zero into the fallback. This
 * only falls back when there is genuinely no number there.
 */
export function parseNumberOr(raw: string, fallback: number): number {
  return parseNumberInput(raw) ?? fallback;
}

/**
 * A field that takes a whole number — a sample size, a word count, a page total.
 *
 * `parseInt` has the same prefix-parsing habit as `parseFloat` and one extra trap of
 * its own: `parseInt("1.000")` is 1 and `parseInt("1,000")` is 1, so a German reader
 * writing a thousand the way German writes it asks for one. Grouping separators are
 * removed here rather than truncated at.
 *
 * Rejects a fractional value outright instead of flooring it. A tool that silently
 * turns "2.5 pages" into 2 is the same class of bug this module exists for, and the
 * caller that genuinely wants rounding can say so.
 */
export function parseIntegerInput(raw: string): number | null {
  const value = parseNumberInput(raw);
  if (value === null || !Number.isInteger(value)) {
    return null;
  }
  return value;
}

/** Semicolon present: it and whitespace separate, and the comma is free to be a decimal. */
const SEMICOLON_SEPARATORS = /[\s;]+/;
/** No semicolon: the comma is a separator, which is what English readers type. */
const COMMA_SEPARATORS = /[\s,;]+/;

/**
 * A field that takes several numbers at once — a list of grades, a data set.
 *
 * These fields have a conflict the single-value fields do not: the comma cannot be both
 * the separator between two numbers and the decimal point inside one. `2,5 3,5` is two
 * numbers to half of Europe and four numbers to everyone else, and no amount of
 * inspection settles it.
 *
 * The rule is the one spreadsheets already settled on, for exactly this reason: **a
 * semicolon anywhere in the text makes the semicolon the separator and frees the comma
 * to be a decimal mark.** `2,5; 3,5` is two numbers. Without a semicolon the comma
 * separates, which is what it did before and what an English reader types.
 *
 * It is deliberately not cleverer than that. A heuristic that guessed from the shape of
 * the tokens would read `1,2,3` as either three numbers or one grouped 123 depending on
 * details no reader can see, and this whole module exists because a number silently
 * became a different number. The failure here is at least visible: a list read with the
 * wrong separator reports the wrong COUNT, and every one of these tools shows n.
 */
export function parseNumberList(raw: string): number[] {
  const separators = raw.includes(";")
    ? SEMICOLON_SEPARATORS
    : COMMA_SEPARATORS;
  const values: number[] = [];
  for (const token of raw.split(separators)) {
    const value = parseNumberInput(token);
    if (value !== null) {
      values.push(value);
    }
  }
  return values;
}
